// "Request a time" on an instructor's profile sidebar. The profile renders it only when no
// upcoming session has a seat left, so a fully booked profile is never a dead end.
//
// A signed-in student picks days, a time of day and an optional note; the instructor gets an
// email. A signed-out visitor is asked to log in first. Instructors and admins cannot send a
// time request, so they do not see the button.
import React from 'react';
import { api } from '../lib/api';
import { useOptionalAuth } from '../contexts/AuthContext';
import { Btn, Modal } from './index';
import { palette as T, type } from '../design';

const FONT = "'Plus Jakarta Sans', sans-serif";

// Mirror WEEKDAYS and TIME_OF_DAY in backend/src/lib/constants.js; the API refuses anything else.
const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];
const TIMES_OF_DAY = [
  { key: 'morning', label: 'Morning', hint: 'before 12:00' },
  { key: 'afternoon', label: 'Afternoon', hint: '12:00 to 17:00' },
  { key: 'evening', label: 'Evening', hint: 'from 17:00' },
];
export const NOTE_MAX = 1000;
const STUDENT_ROLE = 'student';

function toggle(list, key) {
  return list.includes(key) ? list.filter(k => k !== key) : [...list, key];
}

function Chip({ selected, onClick, children }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      style={{
        fontFamily: FONT, fontSize: type.body, fontWeight: 600, cursor: 'pointer',
        padding: '8px 12px', borderRadius: 9999, minHeight: 36,
        border: '1px solid ' + (selected ? T.terracotta : T.border),
        background: selected ? T.terracotta : T.white,
        color: selected ? T.white : T.walnut,
      }}
    >
      {children}
    </button>
  );
}

function Heading({ children, id }) {
  return <h3 id={id} style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h3, color: T.espresso, margin: '0 0 6px', paddingRight: 36 }}>{children}</h3>;
}

function Text({ children, style = {} }) {
  return <p style={{ fontFamily: FONT, fontSize: type.body, color: T.walnut, margin: '0 0 16px', lineHeight: 1.6, ...style }}>{children}</p>;
}

const LABEL_STYLE = { display: 'block', fontFamily: FONT, fontSize: type.body, fontWeight: 700, color: T.espresso, margin: '0 0 8px' };

function Label({ children, htmlFor }) {
  return <label htmlFor={htmlFor} style={LABEL_STYLE}>{children}</label>;
}

// Names a group of toggle chips; the group points at it with aria-labelledby.
function GroupLabel({ children, id }) {
  return <p id={id} style={LABEL_STYLE}>{children}</p>;
}

function SignInPrompt({ name, setPage, onClose }) {
  const titleId = React.useId();
  return (
    <Modal onClose={onClose} maxWidth={440} labelledBy={titleId}>
      <div style={{ padding: '28px 24px' }}>
        <Heading id={titleId}>Log in to request a time</Heading>
        <Text>
          Only students can send a time request. Log in, or create a free student account, then
          come back to {name}&apos;s profile.
        </Text>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Btn onClick={() => setPage('login')}>Log in</Btn>
          <Btn variant="ghost" onClick={() => setPage('register')}>Create an account</Btn>
        </div>
      </div>
    </Modal>
  );
}

function RequestForm({ instructor, name, timezone, onClose }) {
  const [days, setDays] = React.useState([]);
  const [timesOfDay, setTimesOfDay] = React.useState([]);
  const [note, setNote] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState('');
  const [sent, setSent] = React.useState(false);
  const titleId = React.useId();

  const ready = days.length > 0 && timesOfDay.length > 0;

  const submit = async () => {
    if (!ready || sending) return;
    setSending(true);
    setError('');
    try {
      const trimmed = note.trim();
      await api.post('/time-requests', {
        instructorId: instructor.id,
        days,
        timesOfDay,
        ...(trimmed ? { note: trimmed } : {}),
      });
      setSent(true);
    } catch (err) {
      setError(err.data?.message || 'We could not send your time request. Please try again in a minute.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <Modal onClose={onClose} maxWidth={440} labelledBy={titleId}>
        <div style={{ padding: '28px 24px' }} data-testid="time-request-sent">
          <Heading id={titleId}>Time request sent</Heading>
          <Text>
            {name} got your time request by email. We will email you when they publish a session
            for you or answer your request.
          </Text>
          <Btn onClick={onClose}>Close</Btn>
        </div>
      </Modal>
    );
  }

  const footer = (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
      <Btn variant="subtle" onClick={onClose} disabled={sending}>Cancel</Btn>
      <Btn onClick={submit} disabled={!ready || sending}>{sending ? 'Sending…' : 'Send request'}</Btn>
    </div>
  );

  return (
    <Modal onClose={sending ? undefined : onClose} maxWidth={520} footer={footer} labelledBy={titleId}>
      <div style={{ padding: '28px 24px 8px' }}>
        <Heading id={titleId}>Request a time with {name}</Heading>
        <Text>Choose the days and times that suit you. {name} gets an email and can publish a session for you.</Text>

        <div role="group" aria-labelledby="request-time-days" style={{ marginBottom: 18 }}>
          <GroupLabel id="request-time-days">Days</GroupLabel>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {DAYS.map(d => (
              <Chip key={d.key} selected={days.includes(d.key)} onClick={() => setDays(list => toggle(list, d.key))}>{d.label}</Chip>
            ))}
          </div>
        </div>

        <div role="group" aria-labelledby="request-time-times" style={{ marginBottom: 6 }}>
          <GroupLabel id="request-time-times">Time of day</GroupLabel>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {TIMES_OF_DAY.map(t => (
              <Chip key={t.key} selected={timesOfDay.includes(t.key)} onClick={() => setTimesOfDay(list => toggle(list, t.key))}>
                {t.label} <span style={{ fontWeight: 400 }}>({t.hint})</span>
              </Chip>
            ))}
          </div>
        </div>
        {timezone && (
          <p style={{ fontFamily: FONT, fontSize: type.label, color: T.driftwood, margin: '0 0 18px' }}>
            Times are in your timezone: {timezone}.
          </p>
        )}

        <Label htmlFor="request-time-note">Note (optional)</Label>
        <textarea
          id="request-time-note"
          value={note}
          maxLength={NOTE_MAX}
          onChange={e => setNote(e.target.value)}
          rows={3}
          placeholder="For example: I am preparing for a trip in May."
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid ' + T.border,
            fontFamily: FONT, fontSize: type.body, color: T.espresso, resize: 'vertical', outline: 'none',
            boxSizing: 'border-box', background: T.white,
          }}
        />
        <p style={{ fontFamily: FONT, fontSize: type.caption, color: T.driftwood, margin: '4px 0 12px', textAlign: 'right' }}>{note.length} / {NOTE_MAX}</p>

        {!ready && (
          <p style={{ fontFamily: FONT, fontSize: type.body, color: T.driftwood, margin: '0 0 12px' }}>
            Choose at least one day and one time of day to send your request.
          </p>
        )}
        {error && (
          <p role="alert" style={{ fontFamily: FONT, fontSize: type.body, color: T.errorRed, margin: '0 0 12px' }}>{error}</p>
        )}
      </div>
    </Modal>
  );
}

export function RequestTimeButton({ instructor, setPage }) {
  const auth = useOptionalAuth();
  const user = auth?.user || null;
  const [open, setOpen] = React.useState(false);

  // Only students can send one; the API refuses everyone else.
  if (user && user.role !== STUDENT_ROLE) return null;
  if (!instructor) return null;

  const name = instructor.displayName || 'this instructor';
  const close = () => setOpen(false);

  return (
    <div style={{ marginTop: 10 }}>
      <Btn variant="ghost" onClick={() => setOpen(true)} style={{ width: '100%', justifyContent: 'center' }}>
        Request a time
      </Btn>
      <p style={{ fontFamily: FONT, fontSize: type.caption, color: T.driftwood, margin: '6px 0 0', textAlign: 'center' }}>
        No seat left? Ask {name} for a day and time that suits you.
      </p>
      {open && !user && <SignInPrompt name={name} setPage={setPage} onClose={close} />}
      {open && user && <RequestForm instructor={instructor} name={name} timezone={user.timezone} onClose={close} />}
    </div>
  );
}
